import {
  FUEL_ANALYTICS_MAX_DAYS,
  REPORT_RANGE_MAX_DAYS,
  type FuelAnalytics,
  type HomeTrendDay,
  type ReportKey,
  type ReportRangePreset,
  type StationComparisonRow,
} from "@station/contracts";
import { addDays, Decimal, diffDays, type LocalDate, Money } from "@station/core";
import {
  type ChartOption,
  formatChartNumber,
  GRID,
  LEGEND_POSITION,
  VALUE_AXIS_LABEL,
} from "../fuel/chart-options.js";

/**
 * Raporlama ekranlarının SAF yardımcıları (Prompt 22, ADR 0022): dönem hazır ayarları, dışa aktarım sorgusu, kaydedilmiş filtre
 * ↔ ekran durumu dönüşümü ve grafik seçenekleri. Para / miktar hesapları `Money` / `Decimal` ile; yalnızca ÇİZİMDE sayıya
 * çevrilir.
 */

export interface DateRange {
  from: LocalDate;
  to: LocalDate;
}

/** Hazır ayar → dönem (İstanbul "bugün"üne göre; iki uç dahil). */
export function resolveRangePreset(preset: ReportRangePreset, today: LocalDate): DateRange {
  switch (preset) {
    case "TODAY":
      return { from: today, to: today };
    case "YESTERDAY": {
      const yesterday = addDays(today, -1);
      return { from: yesterday, to: yesterday };
    }
    case "LAST_7_DAYS":
      return { from: addDays(today, -6), to: today };
    case "LAST_30_DAYS":
      return { from: addDays(today, -29), to: today };
    case "THIS_MONTH":
      return { from: `${today.slice(0, 8)}01` as LocalDate, to: today };
    case "LAST_MONTH": {
      const lastOfPrevious = addDays(`${today.slice(0, 8)}01` as LocalDate, -1);
      return { from: `${lastOfPrevious.slice(0, 8)}01` as LocalDate, to: lastOfPrevious };
    }
  }
}

/** Dönem bir hazır ayara denk geliyorsa o ayar (seçici "Son 30 gün" gibi gösterir), değilse null. */
export function presetOf(range: DateRange, today: LocalDate): ReportRangePreset | null {
  const presets: ReportRangePreset[] = [
    "TODAY",
    "YESTERDAY",
    "LAST_7_DAYS",
    "LAST_30_DAYS",
    "THIS_MONTH",
    "LAST_MONTH",
  ];
  for (const preset of presets) {
    const resolved = resolveRangePreset(preset, today);
    if (resolved.from === range.from && resolved.to === range.to) return preset;
  }
  return null;
}

/** Rapora göre en uzun dönem (akaryakıt analitiği saatlik veriden → 92 gün; diğerleri 366). */
export const rangeLimitFor = (key: ReportKey | "FUEL_ANALYTICS_PAGE"): number =>
  key === "FUEL_ANALYTICS" || key === "FUEL_ANALYTICS_PAGE" ? FUEL_ANALYTICS_MAX_DAYS : REPORT_RANGE_MAX_DAYS;

/** Dönem hatası (Türkçe) ya da null. */
export function rangeError(range: DateRange, maxDays: number): string | null {
  if (range.to < range.from) return "Başlangıç tarihi bitişten sonra olamaz";
  if (diffDays(range.from, range.to) + 1 > maxDays) return `Dönem en çok ${maxDays} gün olabilir`;
  return null;
}

// ── Kaydedilmiş filtre ↔ ekran durumu ─────────────────────────────────────────

/** Ekranın rapor süzgeci (dönem + kapsam); kaydedilmiş filtrenin `params`'ına ve geri dönüştürülür. */
export interface ReportFilterState {
  range: DateRange;
  preset: ReportRangePreset | null;
  companyId: string | null;
  stationIds: string[];
  /** Akaryakıt analitiği: tek istasyon + fiyat değişimi penceresi. */
  stationId?: string | null;
  windowDays?: number;
}

/** Ekran durumu → kaydedilmiş filtre parametreleri (anahtara göre; dönem yalnızca HAZIR AYAR olarak saklanır). */
export function filterParams(key: ReportKey, state: ReportFilterState): Record<string, unknown> {
  const preset = state.preset ? { rangePreset: state.preset } : {};
  const company = state.companyId ? { companyId: state.companyId } : {};
  switch (key) {
    case "DAILY_SALES":
    case "STATION_COMPARISON":
      return { ...company, stationIds: state.stationIds, ...preset };
    case "FUEL_ANALYTICS":
      return {
        ...company,
        ...(state.stationId ? { stationId: state.stationId } : {}),
        windowDays: state.windowDays ?? 3,
        ...preset,
      };
    case "PROFITABILITY":
      return { ...company, ...preset };
    case "FINANCE_OVERVIEW":
      return company;
    case "MAINTENANCE":
      return { ...company, ...(state.stationId ? { stationId: state.stationId } : {}), ...preset };
  }
}

/** Kaydedilmiş filtre parametreleri → ekran durumu (hazır ayar BUGÜNE göre çözülür; yoksa mevcut dönem korunur). */
export function stateFromParams(
  params: Record<string, unknown>,
  current: ReportFilterState,
  today: LocalDate,
): ReportFilterState {
  const preset = typeof params.rangePreset === "string" ? (params.rangePreset as ReportRangePreset) : null;
  return {
    range: preset ? resolveRangePreset(preset, today) : current.range,
    preset,
    companyId: typeof params.companyId === "string" ? params.companyId : null,
    stationIds: Array.isArray(params.stationIds) ? (params.stationIds as string[]) : [],
    stationId: typeof params.stationId === "string" ? params.stationId : null,
    windowDays: typeof params.windowDays === "number" ? params.windowDays : (current.windowDays ?? 3),
  };
}

/** `GET /reports/{key}/export` sorgusu (düz alanlar; boşlar atlanır). */
export function exportQuery(
  key: ReportKey,
  format: "XLSX" | "PDF",
  state: ReportFilterState,
  extra: { groupBy?: string; includeDrafts?: boolean } = {},
): Record<string, string | number> {
  const query: Record<string, string | number> = { format };
  if (key !== "FINANCE_OVERVIEW") {
    query.from = state.range.from;
    query.to = state.range.to;
  }
  if (state.companyId) query.companyId = state.companyId;
  if ((key === "DAILY_SALES" || key === "STATION_COMPARISON") && state.stationIds.length > 0)
    query.stationIds = state.stationIds.join(",");
  if (key === "MAINTENANCE" && state.stationId) query.stationId = state.stationId;
  if (key === "FUEL_ANALYTICS") {
    if (state.stationId) query.stationId = state.stationId;
    if (state.windowDays) query.windowDays = state.windowDays;
  }
  if (key === "PROFITABILITY") {
    if (extra.groupBy) query.groupBy = extra.groupBy;
    if (extra.includeDrafts) query.includeDrafts = "true";
  }
  return query;
}

// ── Biçimler ──────────────────────────────────────────────────────────────────

/** "12.5" → "%12,5"; "0.02" → "%0,02" (ondalık sayısı değerden); "-18.5" → "−%18,5"; null → "—". */
export function formatPercentValue(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const scale = Math.max(1, (value.split(".")[1] ?? "").length);
  const decimal = Decimal.from(value);
  return `${decimal.isNegative() ? "−" : ""}%${decimal.abs().formatTr(scale)}`;
}

/** Kuruş dizgesi → kısa lira ("1,2 mn ₺", "845 bin ₺", "950 ₺") — yalnızca kart / grafik başlığı için. */
export function formatCompactMoney(value: string | bigint | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const lira = Decimal.from(Money.fromMinor(BigInt(value)).toDecimal().toString());
  const abs = lira.abs();
  if (abs.gte("1000000")) return `${lira.div(1_000_000).formatTr(1)} mn ₺`;
  if (abs.gte("10000")) return `${lira.div(1000).formatTr(0)} bin ₺`;
  return `${lira.formatTr(0)} ₺`;
}

const liraNumber = (minor: string | null | undefined): number | null =>
  minor === null || minor === undefined || minor === ""
    ? null
    : Number(Money.fromMinor(minor).toDecimal().toString());

// ── Grafikler ─────────────────────────────────────────────────────────────────

export interface TrendChartLabels {
  revenue: string;
  litres: string;
  revenueAxis: string;
  litresAxis: string;
}

const shortDate = (date: string) => `${date.slice(8, 10)}.${date.slice(5, 7)}`;

/** Ana sayfa 14 günlük trend: ciro (çizgi, ₺) + pompa litresi (çubuk, L), iki eksen. */
export function trendChartOption(trend: readonly HomeTrendDay[], labels: TrendChartLabels): ChartOption {
  return {
    grid: GRID,
    legend: { ...LEGEND_POSITION, data: [labels.revenue, labels.litres] },
    tooltip: { trigger: "axis", valueFormatter: (value) => formatChartNumber(value) },
    xAxis: { type: "category", data: trend.map((day) => shortDate(day.date)) },
    yAxis: [
      { type: "value", name: labels.revenueAxis, axisLabel: VALUE_AXIS_LABEL },
      { type: "value", name: labels.litresAxis, axisLabel: VALUE_AXIS_LABEL, splitLine: { show: false } },
    ],
    series: [
      {
        name: labels.litres,
        type: "bar",
        yAxisIndex: 1,
        data: trend.map((day) => Number(day.litres)),
        itemStyle: { opacity: 0.45 },
      },
      {
        name: labels.revenue,
        type: "line",
        smooth: true,
        data: trend.map((day) => liraNumber(day.revenue)),
      },
    ],
  };
}

/** İstasyon karşılaştırma: ciroya göre sıralı yatay çubuk (₺). */
export function comparisonChartOption(rows: readonly StationComparisonRow[], label: string): ChartOption {
  const sorted = [...rows]
    .filter((row) => row.revenue !== null)
    .sort((left, right) => (BigInt(left.revenue!) < BigInt(right.revenue!) ? -1 : 1));
  return {
    grid: { ...GRID, left: 24, top: 16 },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      valueFormatter: (value) => `${formatChartNumber(value)} ₺`,
    },
    xAxis: { type: "value", axisLabel: VALUE_AXIS_LABEL },
    yAxis: { type: "category", data: sorted.map((row) => row.stationCode ?? row.stationName) },
    series: [{ name: label, type: "bar", data: sorted.map((row) => liraNumber(row.revenue)) }],
  };
}

export const WEEKDAYS_TR = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"] as const;
const HOURS = Array.from({ length: 24 }, (_unused, hour) => String(hour).padStart(2, "0"));

/** Yoğun saat ısı haritası: hafta günü (satır) × saat (sütun), değer = ORTALAMA litre / saat. */
export function fuelHeatmapOption(heatmap: FuelAnalytics["heatmap"], seriesName: string): ChartOption {
  const data: Array<[number, number, number]> = [];
  let max = 0;
  for (const cell of heatmap) {
    const value = Number(cell.averageLitres);
    max = Math.max(max, value);
    data.push([cell.hour, cell.weekday - 1, value]);
  }
  return {
    grid: { left: 48, right: 24, top: 16, bottom: 72, containLabel: true },
    tooltip: {
      position: "top",
      formatter: (params: unknown) => {
        const value = (params as { value?: [number, number, number] }).value;
        if (!value) return "";
        return `${WEEKDAYS_TR[value[1]] ?? ""} ${HOURS[value[0]]}:00 — ${formatChartNumber(value[2])} L`;
      },
    },
    xAxis: { type: "category", data: HOURS, splitArea: { show: true } },
    yAxis: { type: "category", data: [...WEEKDAYS_TR], inverse: true, splitArea: { show: true } },
    visualMap: {
      min: 0,
      max: Math.max(1, Math.ceil(max)),
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 0,
      inRange: { color: ["#b45309"], colorAlpha: [0.05, 1] },
      formatter: (value: unknown) => `${formatChartNumber(Number(value))} L`,
    },
    series: [
      {
        type: "heatmap",
        name: seriesName,
        data,
        label: { show: false },
        emphasis: { itemStyle: { borderWidth: 1 } },
      },
    ],
  } as ChartOption;
}

/** Isı haritası tablosu: 7 satır × 24 saat ortalama litre (grafiğin erişilebilir karşılığı). */
export function heatmapRows(
  heatmap: FuelAnalytics["heatmap"],
): Array<{ weekday: number; label: string; hours: string[] }> {
  const byKey = new Map(heatmap.map((cell) => [`${cell.weekday}:${cell.hour}`, cell.averageLitres]));
  return WEEKDAYS_TR.map((label, index) => ({
    weekday: index + 1,
    label,
    hours: HOURS.map((_hour, hour) => byKey.get(`${index + 1}:${hour}`) ?? "0.000"),
  }));
}

/** En yoğun N saat dilimi (ortalama litreye göre). */
export function peakHours(
  heatmap: FuelAnalytics["heatmap"],
  count = 3,
): Array<{ weekday: string; hour: string; averageLitres: string }> {
  return [...heatmap]
    .sort((left, right) => Decimal.from(right.averageLitres).cmp(left.averageLitres))
    .slice(0, count)
    .filter((cell) => Decimal.from(cell.averageLitres).gt("0"))
    .map((cell) => ({
      weekday: WEEKDAYS_TR[cell.weekday - 1] ?? "",
      hour: `${HOURS[cell.hour]}:00`,
      averageLitres: cell.averageLitres,
    }));
}

/** Ürün karışımı: litreye göre yatay çubuk. */
export function productMixOption(mix: FuelAnalytics["productMix"], label: string): ChartOption {
  const sorted = [...mix].sort((left, right) => Decimal.from(left.litres).cmp(right.litres));
  return {
    grid: { ...GRID, left: 24, top: 16 },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      valueFormatter: (value) => `${formatChartNumber(value)} L`,
    },
    xAxis: { type: "value", axisLabel: VALUE_AXIS_LABEL },
    yAxis: { type: "category", data: sorted.map((row) => row.productName) },
    series: [{ name: label, type: "bar", data: sorted.map((row) => Number(row.litres)) }],
  };
}

/** İşaretli yüzde değişim için rozet tonu: artış iyi (hacim), azalış uyarı. */
export function changeTone(value: string | null | undefined): "success" | "warning" | "neutral" {
  if (value === null || value === undefined || value === "") return "neutral";
  const decimal = Decimal.from(value);
  if (decimal.gt("0")) return "success";
  if (decimal.lt("0")) return "warning";
  return "neutral";
}
